/**
 * Shared surface + row styles for overflow / action menus (dashboard Menu,
 * editor LayoutOptionMenu). Solid paper + soft shadow only — no backdrop-filter
 * on the panel so menus stay cheap to paint; hover uses a light tint.
 */
import { alpha } from "@mui/material/styles";
import { shapeShell, shapePill, heightRow } from "../theme/shape";

/** Hover fill on menu rows — subtle on both dash + editor menus */
export const actionMenuRowHoverAlpha = 0.06;

/**
 * Symmetric inset between row hover areas and the menu shell edge (6px at
 * theme spacing 8). Divider vertical rhythm matches this value.
 */
export const ACTION_MENU_GUTTER = 0.75;

/** @typedef {'dropdown' | 'bottomSheet' | 'transparent'} ActionMenuPaperVariant */

/**
 * @param {import('@mui/material/styles').Theme} theme
 * @param {ActionMenuPaperVariant} [variant]
 */
export function getActionMenuPaperSx(theme, variant = "dropdown") {
  if (variant === "transparent") {
    return {
      p: 0,
      m: 0,
      overflow: "hidden",
      maxWidth: "100%",
      width: "100%",
      backgroundColor: "transparent",
      backgroundImage: "none",
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
      boxShadow: "none",
      border: "none",
      borderRadius: 0,
    };
  }

  const softShadow = [
    `0 10px 40px ${alpha(theme.palette.common.black, 0.1)}`,
    `0 2px 10px ${alpha(theme.palette.common.black, 0.06)}`,
  ].join(", ");

  if (variant === "bottomSheet") {
    return {
      borderRadius: { xs: "14px 14px 0 0", sm: "16px 16px 0 0" },
      overflow: "hidden",
      width: "100%",
      maxWidth: "100%",
      m: 0,
      backgroundColor: alpha("#ffffff", 0.95),
      backgroundImage: "none",
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
      boxShadow: softShadow,
      border: "none",
      borderTop: `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
    };
  }

  /* Pill rows sit inside with 6px gutter → shapeShell container radius nests cleanly */
  return {
    borderRadius: shapeShell,
    overflow: "hidden",
    width: "100%",
    maxWidth: "100%",
    m: 0,
    backgroundColor: alpha("#ffffff", 0.95),
    backgroundImage: "none",
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    boxShadow: softShadow,
    border: `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
  };
}

/**
 * Scrollable list region inside an action menu (matches prior LayoutOptionMenu padding).
 * @param {'embedded' | 'dialog'} presentation
 */
const actionMenuFlexListSx = {
  gap: ACTION_MENU_GUTTER,
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%",
};

export function getActionMenuListContainerSx(presentation, opts = {}) {
  const isDialog = presentation === "dialog";
  const uniform = opts.uniformSpacing === true;
  return {
    maxHeight: "60vh",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    /* Dialog: gutter matches dashboard actionMenuMenuListSx (8px from shell + row padding). */
    ...(isDialog
      ? {
          py: 0,
          px: 0,
          ...actionMenuFlexListSx,
        }
      : uniform
        ? {
            /* Same rhythm as dashboard overflow + floor-plan dialog (6px shell via parent). */
            py: 0,
            px: 0,
            ...actionMenuFlexListSx,
            boxSizing: "border-box",
          }
        : { py: 1, px: 0.5 }),
  };
}

/**
 * MUI MenuList: symmetric gutter; flex gap matches edge inset so divider
 * spacing equals row-to-edge padding.
 */
export const actionMenuMenuListSx = {
  py: ACTION_MENU_GUTTER,
  px: ACTION_MENU_GUTTER,
  display: "flex",
  flexDirection: "column",
  gap: ACTION_MENU_GUTTER,
  boxSizing: "border-box",
};

/**
 * Inset row hover (icon + label rows in LayoutOptionMenu).
 * @param {import('@mui/material/styles').Theme} theme
 * @param {object} option
 * @param {{ isFloorPlanDialog: boolean }} ctx
 */
export function getLayoutOptionActionRowSx(theme, option, { isFloorPlanDialog, uniformGutter }) {
  const isDanger = option?.danger === true;
  return {
    py: "10px",
    pl: "14px",
    pr: "12px",
    minHeight: heightRow,
    mb: 0,
    mx: isFloorPlanDialog ? 0 : uniformGutter ? 0 : 1.25,
    borderRadius: shapePill,
    alignItems: "center",
    fontSize: "0.875rem",
    fontWeight: 400,
    ...(isDanger && { color: theme.palette.error.main }),
    "&:hover": {
      backgroundColor: isDanger
        ? alpha(theme.palette.error.main, 0.10)
        : alpha(theme.palette.action.hover, actionMenuRowHoverAlpha),
    },
    ...option?.labelStyle,
  };
}

/**
 * Toggle row (MenuItem + switch) in LayoutOptionMenu.
 * @param {import('@mui/material/styles').Theme} theme
 * @param {{ isFloorPlanDialog?: boolean }} [ctx]
 */
export function getActionMenuToggleRowSx(theme, ctx = {}) {
  const isFloorPlanDialog = ctx.isFloorPlanDialog === true;
  const uniformGutter = ctx.uniformGutter === true;
  const suppressHover = ctx.suppressHover === true;
  return {
    py: "10px",
    px: "12px",
    minHeight: heightRow,
    justifyContent: "space-between",
    mx: isFloorPlanDialog ? 0 : uniformGutter ? 0 : 1.25,
    mb: 0,
    borderRadius: shapePill,
    fontSize: "0.875rem",
    fontWeight: 400,
    width: isFloorPlanDialog ? "100%" : undefined,
    boxSizing: isFloorPlanDialog ? "border-box" : undefined,
    ...(suppressHover
      ? {
          "&:hover": {
            backgroundColor: "transparent",
          },
        }
      : {
          "&:hover": {
            backgroundColor: alpha(theme.palette.action.hover, actionMenuRowHoverAlpha),
          },
        }),
  };
}

/**
 * Zero-height list item that draws only a horizontal rule. Parent list flex gap
 * (ACTION_MENU_GUTTER) supplies equal spacing above and below the line.
 * @param {import('@mui/material/styles').Theme} theme
 */
export function getActionMenuDividerLineSx(theme) {
  return {
    listStyle: "none",
    flexShrink: 0,
    alignSelf: "stretch",
    boxSizing: "border-box",
    pointerEvents: "none",
    border: "none",
    borderBottom: `0.5px solid ${alpha(theme.palette.common.black, 0.15)}`,
    mx: 1.5,
    my: 0,
    mt: 0,
    mb: 0,
    py: 0,
    px: 0,
    minHeight: 0,
    height: 0,
    lineHeight: 0,
    overflow: "visible",
  };
}

/** @deprecated Use ActionMenuDividerLine / getActionMenuDividerLineSx. */
export function getActionMenuDividerSx(theme) {
  return getActionMenuDividerLineSx(theme);
}

/** @deprecated Do not put dividers on MenuItem borders. */
export function getActionMenuItemDividerBeforeSx(theme) {
  return getActionMenuDividerLineSx(theme);
}

/**
 * Text-only MenuItem rows (e.g. dashboard account menu).
 * @param {import('@mui/material/styles').Theme} theme
 */
export function getDashboardAccountMenuItemSx(theme) {
  return {
    py: "10px",
    pl: "12px",
    pr: "10px",
    minHeight: heightRow,
    mx: 0,
    my: 0,
    width: "100%",
    boxSizing: "border-box",
    borderRadius: shapePill,
    fontSize: "0.875rem",
    fontWeight: 400,
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: alpha(theme.palette.action.hover, actionMenuRowHoverAlpha),
    },
  };
}

/**
 * Destructive row variant for dashboard MenuItem (e.g. Log out).
 * @param {import('@mui/material/styles').Theme} theme
 */
export function getDashboardAccountMenuItemDangerSx(theme) {
  return {
    ...getDashboardAccountMenuItemSx(theme),
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.10),
    },
  };
}

/** @deprecated Use ActionMenuDividerLine / getActionMenuDividerLineSx. */
export function getActionMenuDividerCompactSx(theme) {
  return getActionMenuDividerLineSx(theme);
}
